import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Trophy, Sparkles, Brain, ArrowRight, Calendar, GraduationCap,
  ScanText, Crown, Star, Zap, ShieldCheck, Target, BookOpen,
  CheckCircle2, ArrowUpRight, Gift, Rocket, Activity,
} from 'lucide-react';

const REWARD_BRANDS = [
  { name: 'Amazon',    accent: '#FF9900' },
  { name: 'Flipkart',  accent: '#2874F0' },
  { name: 'Unacademy', accent: '#10B981' },
  { name: 'Aakash',    accent: '#06B6D4' },
  { name: 'Motion',    accent: '#A855F7' },
];

const FEATURES = [
  { Icon: Calendar,      title: 'Daily AI Mock Tests',       tag: 'PRO',  copy: 'Fresh 30Q / 45min mock every day, tuned to JEE (Mains+Advanced) or NEET. Subject-wise analytics + per-question explanations.' },
  { Icon: BookOpen,      title: 'Weekly Full-Length Mocks',  tag: 'FREE', copy: 'One free full-length mock per week (75Q JEE Mains, 54Q JEE Advanced, or 90Q NEET). Real exam pattern.' },
  { Icon: ScanText,      title: 'AI Doubt Solver',           tag: null,   copy: 'Snap a photo of any doubt — Claude Vision walks you through the full solution. 5 free/day; unlimited with Pro.' },
  { Icon: Trophy,        title: 'Skill-Based Quiz Pools',    tag: null,   copy: 'Compete in topic pools with fellow aspirants. Top 50% win points back. Face + device detection keeps it fair.' },
  { Icon: GraduationCap, title: 'College Rank Predictor',    tag: null,   copy: 'Enter your mock score → get an All-India rank estimate + IIT/NIT/AIIMS shortlist with Dream/Target/Safety tags.' },
  { Icon: Zap,           title: 'Daily Streak Bonuses',      tag: null,   copy: '30+ questions a day keeps your streak alive. Hit milestones for bonus points, unlock badges, stay consistent.' },
  { Icon: ShieldCheck,   title: 'Proctored Quizzes',         tag: null,   copy: 'Real-time face presence + banned-device detection (TensorFlow.js, on-device). Your rank is always honest.' },
  { Icon: Rocket,        title: 'Chapter-wise AI Courses',   tag: 'PRO',  copy: 'Video lessons + mastery quizzes for every JEE/NEET chapter. Plus a 15-day crash-course pass (₹499 standalone).' },
];

const STATS = [
  { label: 'Active Aspirants',          value: '12,400+' },
  { label: 'AI Quizzes Delivered',      value: '480K+' },
  { label: 'Super Points paid out (₹)', value: '18.6L+' },
  { label: 'Institutes ranked',         value: '15+' },
];

const LandingPage = () => {
  const [time, setTime] = useState('');
  useEffect(() => {
    const tick = () => setTime(new Date().toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' }));
    tick();
    const t = setInterval(tick, 30000);
    return () => clearInterval(t);
  }, []);

  return (
    <div className="min-h-screen" data-testid="landing-page">

      {/* ============== TOP NAV ============== */}
      <nav
        className="sticky top-0 z-40 backdrop-blur-xl border-b"
        style={{ background: 'rgb(var(--bg) / 0.75)', borderColor: 'rgb(var(--border))' }}
      >
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 py-3 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2.5" data-testid="landing-logo">
            <img src="/aptivon-logo-v2.png" alt="Aptivon" className="h-10 sm:h-11 w-auto" />
            <span className="hidden sm:block text-fg-faint text-[10px] uppercase tracking-widest">JEE · NEET OS</span>
          </Link>
          <div className="hidden md:flex items-center gap-6 text-sm">
            <a href="#features" className="text-fg-muted hover:text-fg transition">Features</a>
            <a href="#rewards" className="text-fg-muted hover:text-fg transition">Rewards</a>
            <a href="#pricing" className="text-fg-muted hover:text-fg transition">Pricing</a>
            <Link to="/for-institutes" className="text-fg-muted hover:text-fg transition">Institutes</Link>
          </div>
          <div className="flex items-center gap-2">
            <Link to="/login" className="px-3 py-1.5 rounded-md text-sm font-medium text-fg-muted hover:text-fg transition" data-testid="nav-login">
              Sign in
            </Link>
            <Link to="/register"
              className="px-3.5 py-1.5 rounded-md text-sm font-semibold text-white transition hover:brightness-110"
              style={{ background: 'rgb(var(--accent))' }}
              data-testid="nav-register">
              Start free
            </Link>
          </div>
        </div>
      </nav>

      {/* ============== HERO ============== */}
      <section className="relative overflow-hidden" data-testid="hero-section">
        <div className="absolute inset-0 pointer-events-none opacity-70"
          style={{ background: 'radial-gradient(circle at 15% 25%, rgba(239,68,68,0.20), transparent 45%), radial-gradient(circle at 85% 70%, rgba(239,68,68,0.10), transparent 45%)' }} />
        <div className="relative max-w-[1400px] mx-auto px-4 sm:px-6 pt-16 pb-20 grid grid-cols-1 lg:grid-cols-[1.05fr_1fr] gap-12 items-center">

          {/* Left column: pitch */}
          <div>
            <div className="inline-flex items-center gap-2 mb-5 text-[11px] uppercase tracking-widest text-fg-faint font-medium">
              <Sparkles className="w-3 h-3" style={{ color: 'rgb(var(--accent))' }} />
              <span>AI-Powered · Skill-based · Freemium</span>
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-fg tracking-tight leading-[1.05] mb-5" data-testid="hero-title">
              Crack JEE &amp; NEET with{' '}
              <span style={{ color: 'rgb(var(--accent))' }}>daily AI mocks</span>{' '}
              &amp; a{' '}
              <span style={{ color: 'rgb(var(--accent))' }}>reward wallet</span>{' '}
              that actually pays.
            </h1>
            <p className="text-base sm:text-lg text-fg-muted max-w-xl mb-8 leading-relaxed">
              Fresh 30-question mocks every morning. 1v1 quiz battles. Real gift cards for top-3% performers.
              Built by aspirants who cracked the exam — and got tired of static PDFs.
            </p>
            <div className="flex flex-wrap items-center gap-3 mb-8">
              <Link to="/register"
                className="group inline-flex items-center gap-2 px-5 py-3 rounded-md text-sm font-semibold text-white transition hover:brightness-110"
                style={{ background: 'rgb(var(--accent))' }}
                data-testid="hero-cta-register">
                Start free — 100 points on us
                <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition" />
              </Link>
              <Link to="/login"
                className="inline-flex items-center gap-2 px-5 py-3 rounded-md text-sm font-semibold border text-fg transition hover:bg-card-hover"
                style={{ background: 'rgb(var(--card))', borderColor: 'rgb(var(--border))' }}
                data-testid="hero-cta-login">
                Sign in
              </Link>
            </div>
            <div className="flex flex-wrap items-center gap-4 text-xs text-fg-faint">
              <span className="inline-flex items-center gap-1.5"><CheckCircle2 className="w-3.5 h-3.5" style={{ color: 'rgb(16,185,129)' }} /> No credit card</span>
              <span className="inline-flex items-center gap-1.5"><CheckCircle2 className="w-3.5 h-3.5" style={{ color: 'rgb(16,185,129)' }} /> Cancel Pro anytime</span>
              <span className="inline-flex items-center gap-1.5"><CheckCircle2 className="w-3.5 h-3.5" style={{ color: 'rgb(16,185,129)' }} /> Age 15+ · DPDP-compliant</span>
            </div>
          </div>

          {/* Right column: NKOS-style live dashboard preview */}
          <div className="relative">
            <div
              className="rounded-xl border shadow-2xl overflow-hidden"
              style={{ background: 'rgb(var(--card))', borderColor: 'rgb(var(--border))' }}
              data-testid="hero-preview-card"
            >
              {/* Chrome bar */}
              <div className="flex items-center justify-between px-4 py-2.5 border-b" style={{ borderColor: 'rgb(var(--border))', background: 'rgb(var(--bg-elevated))' }}>
                <div className="flex items-center gap-1.5">
                  <div className="w-2.5 h-2.5 rounded-full" style={{ background: '#FF5F57' }} />
                  <div className="w-2.5 h-2.5 rounded-full" style={{ background: '#FEBC2E' }} />
                  <div className="w-2.5 h-2.5 rounded-full" style={{ background: '#28C840' }} />
                </div>
                <div className="text-[11px] font-mono text-fg-faint">aptivon.in / dashboard</div>
                <div className="inline-flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-widest" style={{ color: 'rgb(16,185,129)' }}>
                  <span className="relative flex h-1.5 w-1.5">
                    <span className="absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75 animate-ping" />
                    <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-emerald-400" />
                  </span>
                  Live
                </div>
              </div>
              {/* Body */}
              <div className="p-5 space-y-4">
                <div>
                  <p className="text-[10px] font-medium uppercase tracking-widest text-fg-faint">Executive Brief · today · {time || '—'}</p>
                  <h3 className="text-2xl font-bold text-fg tracking-tight mt-1">
                    Good evening, <span style={{ color: 'rgb(var(--accent))' }}>Aarav</span>.
                  </h3>
                  <p className="text-xs text-fg-muted mt-0.5">2,340 points · 12-day streak · 3 live pools waiting.</p>
                </div>

                {/* Reward wallet mini */}
                <div
                  className="relative overflow-hidden rounded-lg border p-4"
                  style={{
                    background: 'linear-gradient(135deg, rgb(var(--card)) 0%, rgba(239,68,68,0.10) 100%)',
                    borderColor: 'rgba(239,68,68,0.35)',
                  }}
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-[10px] font-medium uppercase tracking-widest text-fg-faint">Reward Wallet</p>
                      <div className="flex items-baseline gap-1.5 mt-0.5">
                        <Star className="w-5 h-5 fill-current" style={{ color: 'rgb(var(--accent))' }} />
                        <span className="text-3xl font-bold text-fg tracking-tight">7,800</span>
                        <span className="text-xs text-fg-muted">Super Points</span>
                      </div>
                      <p className="text-[11px] text-fg-muted">≈ ₹3,900 redeemable</p>
                    </div>
                    <div className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-md text-[11px] font-semibold text-white"
                      style={{ background: 'rgb(var(--accent))' }}>
                      <Gift className="w-3 h-3" /> Redeem
                    </div>
                  </div>
                </div>

                {/* Stat grid */}
                <div className="grid grid-cols-3 gap-2">
                  {[
                    { l: 'Points',    v: '2,340', d: '+180' },
                    { l: 'Win rate',  v: '68%',   d: '+4%' },
                    { l: 'Streak',    v: '12d',   d: '+1' },
                  ].map((s, i) => (
                    <div key={i} className="rounded-md border p-2.5" style={{ borderColor: 'rgb(var(--border))' }}>
                      <p className="text-[9px] font-medium uppercase tracking-widest text-fg-faint">{s.l}</p>
                      <div className="flex items-baseline gap-1 mt-0.5">
                        <span className="text-lg font-bold text-fg">{s.v}</span>
                        <span className="text-[10px] font-semibold" style={{ color: 'rgb(16,185,129)' }}>{s.d}</span>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Fake activity ticker */}
                <div className="rounded-md border p-2.5" style={{ borderColor: 'rgb(var(--border))' }}>
                  <p className="text-[9px] font-medium uppercase tracking-widest text-fg-faint mb-1.5">Live activity</p>
                  <p className="text-xs text-fg-secondary">
                    <span className="font-mono" style={{ color: 'rgb(var(--accent))' }}>NK-042</span>
                    &nbsp;— Anvi finished today&apos;s Physics mock, <span className="text-fg font-semibold">92%</span>
                  </p>
                </div>
              </div>
            </div>
            {/* Floating accent */}
            <div className="absolute -bottom-4 -right-4 w-24 h-24 rounded-full blur-3xl pointer-events-none"
              style={{ background: 'rgba(239,68,68,0.30)' }} />
          </div>
        </div>
      </section>

      {/* ============== STATS BAND ============== */}
      <section className="border-y" style={{ borderColor: 'rgb(var(--border))' }} data-testid="stats-band">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 py-8 grid grid-cols-2 lg:grid-cols-4 gap-6">
          {STATS.map((s, i) => (
            <div key={i} className="text-left">
              <p className="text-[10px] font-medium uppercase tracking-widest text-fg-faint mb-1">{s.label}</p>
              <p className="text-3xl font-bold text-fg tracking-tight">{s.value}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ============== REWARDS SPOTLIGHT ============== */}
      <section id="rewards" className="py-20" data-testid="rewards-section">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6">
          <div className="max-w-2xl mb-12">
            <div className="inline-flex items-center gap-2 mb-3 text-[11px] uppercase tracking-widest text-fg-faint font-medium">
              <Star className="w-3 h-3 fill-current" style={{ color: 'rgb(var(--accent))' }} />
              <span>The Reward Wallet</span>
            </div>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-fg tracking-tight mb-4">
              Real gift cards. <span style={{ color: 'rgb(var(--accent))' }}>Real outcomes.</span>
            </h2>
            <p className="text-base text-fg-muted leading-relaxed">
              Every question you nail earns points. Every rank you climb earns Super Points (⭐).
              Top-3% Pro performers convert <span className="text-fg font-semibold">1 ⭐ = ₹0.50</span> straight into Amazon, Flipkart, Unacademy, Aakash and Motion gift cards.
              No sign-up hoops. No expiry. Just cash-outs for grinding.
            </p>
          </div>

          {/* Big illustrative wallet card */}
          <div className="grid grid-cols-1 lg:grid-cols-[1.2fr_1fr] gap-6 mb-10">
            <div
              className="relative overflow-hidden rounded-xl border p-6 sm:p-8"
              style={{
                background: 'linear-gradient(135deg, rgb(var(--card)) 0%, rgba(239,68,68,0.10) 100%)',
                borderColor: 'rgba(239,68,68,0.35)',
              }}
              data-testid="reward-illustration-card"
            >
              <div className="absolute -top-20 -right-20 w-72 h-72 rounded-full blur-3xl pointer-events-none"
                style={{ background: 'rgba(239,68,68,0.22)' }} />
              <div className="relative">
                <p className="text-[10px] font-medium uppercase tracking-widest text-fg-faint mb-3">Sample monthly payout · Rank #37</p>
                <div className="flex items-baseline gap-3 mb-3">
                  <Star className="w-10 h-10 fill-current" style={{ color: 'rgb(var(--accent))' }} />
                  <span className="text-6xl font-bold text-fg tracking-tight">4,800</span>
                  <span className="text-base text-fg-muted">Super Points</span>
                </div>
                <p className="text-lg text-fg-muted mb-6">
                  = <span className="text-fg font-semibold">₹2,400</span> in gift cards, delivered to your inbox next morning.
                </p>
                <div className="grid grid-cols-2 sm:grid-cols-5 gap-2.5">
                  {REWARD_BRANDS.map((b) => (
                    <div
                      key={b.name}
                      className="rounded-md border px-3 py-2.5 text-center transition hover:-translate-y-0.5"
                      style={{ background: 'rgb(var(--card))', borderColor: 'rgb(var(--border))' }}
                    >
                      <div className="w-6 h-6 mx-auto rounded-md mb-1.5" style={{ background: `${b.accent}22` }}>
                        <div className="w-full h-full rounded-md" style={{ background: `${b.accent}` }} />
                      </div>
                      <p className="text-[11px] font-semibold text-fg">{b.name}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Right: how to earn */}
            <div
              className="rounded-xl border p-6 sm:p-8"
              style={{ background: 'rgb(var(--card))', borderColor: 'rgb(var(--border))' }}
              data-testid="how-to-earn-card"
            >
              <p className="text-[10px] font-medium uppercase tracking-widest text-fg-faint mb-4">How you earn Super Points</p>
              <ul className="space-y-4">
                {[
                  { rank: 'Rank #1',   pts: '15,000', money: '₹7,500' },
                  { rank: 'Top 10',    pts: '5,000',  money: '₹2,500' },
                  { rank: 'Top 100',   pts: '1,200',  money: '₹600' },
                  { rank: 'Top 3% overall',  pts: '300',    money: '₹150' },
                ].map((r, i) => (
                  <li key={i} className="flex items-center gap-3 pb-3 last:pb-0 border-b last:border-b-0" style={{ borderColor: 'rgb(var(--border))' }}>
                    <div className="w-7 h-7 rounded-md flex items-center justify-center shrink-0 text-[10px] font-bold" style={{ background: 'rgba(239,68,68,0.14)', color: 'rgb(var(--accent))' }}>
                      #{i+1}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-fg">{r.rank}</p>
                      <p className="text-[11px] text-fg-muted">Every month on the Pro leaderboard</p>
                    </div>
                    <div className="text-right shrink-0">
                      <p className="text-sm font-bold text-fg tracking-tight">
                        <Star className="w-3.5 h-3.5 inline-block fill-current mr-0.5" style={{ color: 'rgb(var(--accent))' }} />
                        {r.pts}
                      </p>
                      <p className="text-[11px] text-fg-muted">≈ {r.money}</p>
                    </div>
                  </li>
                ))}
              </ul>
              <div className="mt-5 flex items-start gap-2 p-3 rounded-md" style={{ background: 'rgb(var(--card-hover))' }}>
                <ShieldCheck className="w-3.5 h-3.5 shrink-0 mt-0.5" style={{ color: 'rgb(16,185,129)' }} />
                <p className="text-[11px] text-fg-muted leading-relaxed">
                  100% skill-based academic assessment. <span className="text-fg">Not gambling / RMG.</span> Bots never receive payouts — only real aspirants ranked on merit.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ============== FEATURE GRID ============== */}
      <section id="features" className="py-20 border-t" style={{ borderColor: 'rgb(var(--border))' }} data-testid="features-section">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6">
          <div className="max-w-2xl mb-10">
            <div className="inline-flex items-center gap-2 mb-3 text-[11px] uppercase tracking-widest text-fg-faint font-medium">
              <Activity className="w-3 h-3" style={{ color: 'rgb(var(--accent))' }} />
              <span>Platform</span>
            </div>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-fg tracking-tight mb-3">
              Eight modules. One operating system for your prep.
            </h2>
            <p className="text-base text-fg-muted">
              Every ritual of a serious aspirant — daily practice, doubts, mocks, leaderboards, rewards — connected end-to-end.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {FEATURES.map(({ Icon, title, copy, tag }, i) => (
              <div
                key={i}
                className="group rounded-xl border p-5 transition hover:-translate-y-0.5 hover:border-app-strong"
                style={{ background: 'rgb(var(--card))', borderColor: 'rgb(var(--border))' }}
                data-testid={`feature-${i}`}
              >
                <div className="flex items-start justify-between mb-3">
                  <div className="w-9 h-9 rounded-md flex items-center justify-center"
                    style={{ background: 'rgba(239,68,68,0.12)' }}>
                    <Icon className="w-4 h-4" style={{ color: 'rgb(var(--accent))' }} />
                  </div>
                  {tag && (
                    <span className="text-[9px] font-bold uppercase tracking-widest px-1.5 py-0.5 rounded"
                      style={{
                        background: tag === 'PRO' ? 'rgba(245,158,11,0.14)' : 'rgba(16,185,129,0.14)',
                        color: tag === 'PRO' ? 'rgb(245,158,11)' : 'rgb(16,185,129)',
                      }}>
                      {tag}
                    </span>
                  )}
                </div>
                <h3 className="font-semibold text-fg mb-1.5 text-[15px]">{title}</h3>
                <p className="text-xs text-fg-muted leading-relaxed">{copy}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ============== PRICING ============== */}
      <section id="pricing" className="py-20 border-t" style={{ borderColor: 'rgb(var(--border))' }} data-testid="pricing-section">
        <div className="max-w-[1100px] mx-auto px-4 sm:px-6">
          <div className="max-w-xl mb-10">
            <div className="inline-flex items-center gap-2 mb-3 text-[11px] uppercase tracking-widest text-fg-faint font-medium">
              <Crown className="w-3 h-3" style={{ color: 'rgb(var(--accent))' }} />
              <span>Pricing</span>
            </div>
            <h2 className="text-3xl sm:text-4xl font-bold text-fg tracking-tight mb-3">
              Two paid tracks. One free forever.
            </h2>
            <p className="text-base text-fg-muted">
              Cancel Pro anytime. Crash-course is a single ₹499 charge — no recurring gotchas.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Free */}
            <div
              className="rounded-xl border p-6"
              style={{ background: 'rgb(var(--card))', borderColor: 'rgb(var(--border))' }}
              data-testid="plan-free"
            >
              <p className="text-[10px] font-medium uppercase tracking-widest text-fg-faint mb-2">Free Forever</p>
              <p className="text-4xl font-bold text-fg tracking-tight">₹0</p>
              <p className="text-xs text-fg-muted mt-1 mb-6">100 starter points · No card</p>
              <ul className="space-y-2 text-sm">
                <FreeRow text="1 weekly full-length mock" />
                <FreeRow text="5 AI doubt solutions / day" />
                <FreeRow text="Quiz pools + leaderboards" />
                <FreeRow text="Daily streak bonuses" />
                <FreeRow text="Affiliate course discounts" />
              </ul>
              <Link to="/register" className="mt-6 w-full inline-flex items-center justify-center px-4 py-2.5 rounded-md border text-sm font-semibold text-fg transition hover:bg-card-hover"
                style={{ borderColor: 'rgb(var(--border))' }}>
                Start free
              </Link>
            </div>

            {/* Crash-course */}
            <div
              className="rounded-xl border p-6"
              style={{ background: 'rgb(var(--card))', borderColor: 'rgb(var(--border))' }}
              data-testid="plan-crash"
            >
              <p className="text-[10px] font-medium uppercase tracking-widest text-fg-faint mb-2">15-Day Crash Pass</p>
              <p className="text-4xl font-bold text-fg tracking-tight">₹499<span className="text-sm font-medium text-fg-muted"> once</span></p>
              <p className="text-xs text-fg-muted mt-1 mb-6">30-day access · No renewal</p>
              <ul className="space-y-2 text-sm">
                <FreeRow text="High-yield 15-day sprint" checked />
                <FreeRow text="3 subjects/day · 90 min" checked />
                <FreeRow text="AI formula sheets + PYQ drills" checked />
                <FreeRow text="Chapter mastery quizzes" checked />
                <FreeRow text="Choose JEE or NEET" checked />
              </ul>
              <Link to="/register?next=/crash-course" className="mt-6 w-full inline-flex items-center justify-center px-4 py-2.5 rounded-md border text-sm font-semibold text-fg transition hover:bg-card-hover"
                style={{ borderColor: 'rgb(var(--border))' }}>
                Buy crash-course
              </Link>
            </div>

            {/* Pro */}
            <div
              className="relative rounded-xl border p-6 overflow-hidden"
              style={{
                background: 'linear-gradient(135deg, rgb(var(--card)) 0%, rgba(239,68,68,0.12) 100%)',
                borderColor: 'rgba(239,68,68,0.55)',
              }}
              data-testid="plan-pro"
            >
              <div className="absolute -top-16 -right-16 w-48 h-48 rounded-full blur-3xl pointer-events-none"
                style={{ background: 'rgba(239,68,68,0.22)' }} />
              <span className="absolute top-4 right-4 px-2 py-0.5 rounded-md text-[9px] font-bold uppercase tracking-widest"
                style={{ background: 'rgb(var(--accent))', color: '#fff' }}>Most chosen</span>
              <p className="text-[10px] font-medium uppercase tracking-widest text-fg-faint mb-2 relative">Aptivon Pro</p>
              <div className="relative">
                <p className="text-4xl font-bold text-fg tracking-tight">₹299<span className="text-sm font-medium text-fg-muted">/mo</span></p>
                <p className="text-xs text-fg-muted mt-1 mb-6">or ₹2,499/yr — save 30%</p>
                <ul className="space-y-2 text-sm">
                  <FreeRow text="Daily AI mock (30Q · 45min)" checked accent />
                  <FreeRow text="Unlimited weekly full-length mocks" checked accent />
                  <FreeRow text="Unlimited AI doubt solutions" checked accent />
                  <FreeRow text="500 monthly bonus points" checked accent />
                  <FreeRow text="Top-3% → up to 15,000 ⭐" checked accent />
                  <FreeRow text="All AI courses + crash-course included" checked accent />
                </ul>
                <Link to="/register" className="mt-6 w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-md text-sm font-semibold text-white transition hover:brightness-110"
                  style={{ background: 'rgb(var(--accent))' }}
                  data-testid="pricing-cta-pro">
                  Start 7-day free trial <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ============== HOW IT WORKS ============== */}
      <section className="py-20 border-t" style={{ borderColor: 'rgb(var(--border))' }} data-testid="how-it-works-section">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6">
          <div className="mb-10">
            <div className="inline-flex items-center gap-2 mb-3 text-[11px] uppercase tracking-widest text-fg-faint font-medium">
              <Target className="w-3 h-3" style={{ color: 'rgb(var(--accent))' }} />
              <span>Playbook</span>
            </div>
            <h2 className="text-3xl sm:text-4xl font-bold text-fg tracking-tight max-w-xl">
              How you go from signup to cashing out ⭐
            </h2>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {[
              { n: '01', Icon: Target, title: 'Pick your track',  copy: 'Choose JEE (Mains + Advanced) or NEET. Every mock, quiz and drill tunes to your syllabus.' },
              { n: '02', Icon: Brain,  title: 'Practise daily',   copy: 'Daily AI mock, quick doubts, chapter mastery quizzes. Streaks earn bonus points.' },
              { n: '03', Icon: Trophy, title: 'Climb the board',  copy: 'Monthly Pro leaderboard ranks you across 15+ institutes. Cross-institute means real position.' },
              { n: '04', Icon: Gift,   title: 'Cash out ⭐',       copy: 'Top-3% converts Super Points at 1 ⭐ = ₹0.50 into Amazon / Flipkart / Unacademy gift cards.' },
            ].map((s) => (
              <div key={s.n} className="rounded-xl border p-5"
                style={{ background: 'rgb(var(--card))', borderColor: 'rgb(var(--border))' }}
                data-testid={`step-${s.n}`}>
                <div className="flex items-center justify-between mb-4">
                  <span className="font-mono text-[11px] text-fg-faint">{s.n}</span>
                  <s.Icon className="w-4 h-4" style={{ color: 'rgb(var(--accent))' }} />
                </div>
                <h3 className="font-semibold text-fg mb-1.5">{s.title}</h3>
                <p className="text-xs text-fg-muted leading-relaxed">{s.copy}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ============== FINAL CTA ============== */}
      <section className="py-24 border-t" style={{ borderColor: 'rgb(var(--border))' }} data-testid="final-cta">
        <div className="max-w-[900px] mx-auto px-4 sm:px-6 text-center">
          <div className="inline-flex items-center gap-2 mb-4 text-[11px] uppercase tracking-widest text-fg-faint font-medium">
            <ArrowUpRight className="w-3 h-3" style={{ color: 'rgb(var(--accent))' }} />
            <span>Ready when you are</span>
          </div>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-fg tracking-tight mb-4">
            Stop reading. Start earning your rank.
          </h2>
          <p className="text-base text-fg-muted mb-8 max-w-lg mx-auto">
            Join thousands of JEE &amp; NEET aspirants already on Aptivon. First AI mock takes 45 minutes.
            Your first ₹100 gift card is closer than you think.
          </p>
          <div className="flex flex-wrap items-center justify-center gap-3">
            <Link to="/register"
              className="inline-flex items-center gap-2 px-6 py-3.5 rounded-md text-sm font-semibold text-white transition hover:brightness-110"
              style={{ background: 'rgb(var(--accent))' }}
              data-testid="final-cta-register">
              Create free account <ArrowRight className="w-4 h-4" />
            </Link>
            <Link to="/login"
              className="inline-flex items-center gap-2 px-6 py-3.5 rounded-md text-sm font-semibold border text-fg transition hover:bg-card-hover"
              style={{ background: 'rgb(var(--card))', borderColor: 'rgb(var(--border))' }}
              data-testid="final-cta-login">
              I already have one
            </Link>
          </div>
        </div>
      </section>

      {/* ============== FOOTER ============== */}
      <footer className="border-t py-12" style={{ borderColor: 'rgb(var(--border))' }} data-testid="landing-footer">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6">
          <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-8">
            <div className="max-w-md">
              <div className="flex items-center gap-2.5 mb-3">
                <img src="/aptivon-logo-v2.png" alt="Aptivon" className="h-11 w-auto" />
                <span className="text-fg-faint text-[10px] uppercase tracking-widest">JEE · NEET OS</span>
              </div>
              <p className="text-xs text-fg-muted leading-relaxed">
                A freemium educational SaaS for JEE &amp; NEET aspirants. All competitions are skill-based academic assessments and are <span className="text-fg-secondary">not gambling or RMG</span>.
                Age 15+ (guardian consent if under 18). Practise responsibly.
              </p>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-8 text-sm">
              <div>
                <p className="text-[10px] font-medium uppercase tracking-widest text-fg-faint mb-3">Product</p>
                <ul className="space-y-2">
                  <li><a href="#features" className="text-fg-muted hover:text-fg transition">Features</a></li>
                  <li><a href="#rewards" className="text-fg-muted hover:text-fg transition">Rewards</a></li>
                  <li><a href="#pricing" className="text-fg-muted hover:text-fg transition">Pricing</a></li>
                </ul>
              </div>
              <div>
                <p className="text-[10px] font-medium uppercase tracking-widest text-fg-faint mb-3">B2B</p>
                <ul className="space-y-2">
                  <li><Link to="/for-institutes" className="text-fg-muted hover:text-fg transition">For Institutes</Link></li>
                </ul>
              </div>
              <div>
                <p className="text-[10px] font-medium uppercase tracking-widest text-fg-faint mb-3">Legal</p>
                <ul className="space-y-2">
                  <li><Link to="/terms" className="text-fg-muted hover:text-fg transition">Terms</Link></li>
                  <li><Link to="/privacy" className="text-fg-muted hover:text-fg transition">Privacy</Link></li>
                  <li><a href="mailto:support@aptivon.in" className="text-fg-muted hover:text-fg transition">Contact</a></li>
                </ul>
              </div>
            </div>
          </div>
          <div className="mt-10 pt-6 border-t flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3" style={{ borderColor: 'rgb(var(--border))' }}>
            <p className="text-[11px] text-fg-faint">© 2026 Aptivon · Made for JEE &amp; NEET aspirants.</p>
            <p className="text-[11px] font-mono text-fg-faint">v1.0 · Aptivon OS</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

const FreeRow = ({ text, checked, accent }) => (
  <li className="flex items-start gap-2">
    <CheckCircle2
      className="w-3.5 h-3.5 shrink-0 mt-0.5"
      style={{ color: accent ? 'rgb(var(--accent))' : checked ? 'rgb(16,185,129)' : 'rgb(var(--fg-faint))' }}
    />
    <span className="text-fg-secondary">{text}</span>
  </li>
);

export default LandingPage;
